import { useMemo, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import {
  useFinancialCashFlow,
  useFinancialDashboard,
  useFinancialOverdue,
} from "../../../hooks/useFinancialDashboard";
import { useSendPaymentReminder } from "../../../hooks/usePayment";

const MONTH_OPTIONS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return amount.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDate(value) {
  if (!value) return "—";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("pt-BR");
}

function buildYearOptions(centerYear) {
  const years = [];
  for (let y = centerYear - 5; y <= centerYear + 1; y++) {
    years.push(y);
  }
  return years;
}

function FinancialDashboard() {
  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(currentYear);
  const [cashFlowYear, setCashFlowYear] = useState(currentYear);

  const yearOptions = useMemo(
    () => buildYearOptions(currentYear),
    [currentYear]
  );

  const { kpis, isLoading } = useFinancialDashboard({ month, year });
  const { months: cashFlowMonths, isLoading: cashFlowLoading } =
    useFinancialCashFlow({ year: cashFlowYear });
  const { overdue, totals, isLoading: overdueLoading } =
    useFinancialOverdue();
  const { sendReminder, isSendingReminder } = useSendPaymentReminder();
  const [sendingReminderId, setSendingReminderId] = useState(null);

  const handleSendReminder = async (paymentId) => {
    setSendingReminderId(paymentId);
    try {
      await sendReminder(paymentId);
    } catch (_) {
      // Toast handled in useSendPaymentReminder onError.
    } finally {
      setSendingReminderId(null);
    }
  };

  const kpiCards = [
    {
      key: "to_receive",
      label: "A receber no mês",
      value: formatCurrency(kpis.to_receive),
      hint: "Saldo em aberto com vencimento no mês",
    },
    {
      key: "received",
      label: "Recebido no mês",
      value: formatCurrency(kpis.received),
      hint: "Confirmado no período",
    },
    {
      key: "expenses",
      label: "Despesas do mês",
      value: formatCurrency(kpis.expenses),
      hint: "Despesas pagas no período",
    },
    {
      key: "net_profit",
      label: "Lucro líquido",
      value: formatCurrency(kpis.net_profit),
      hint: "Recebido − despesas",
      tone:
        Number(kpis.net_profit) < 0
          ? "negative"
          : Number(kpis.net_profit) > 0
            ? "positive"
            : "",
    },
    {
      key: "overdue_clients_count",
      label: "Clientes inadimplentes",
      value: String(kpis.overdue_clients_count ?? 0),
      hint: "Com cobranças vencidas",
      tone: Number(kpis.overdue_clients_count) > 0 ? "warning" : "",
    },
    {
      key: "upcoming_dues",
      label: "Próximos vencimentos",
      value: String(kpis.upcoming_dues_count ?? 0),
      hint: formatCurrency(kpis.upcoming_dues_amount),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-0">Dashboard</h5>
          <small className="text-muted">
            Visão geral do mês, fluxo de caixa e inadimplência.
          </small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Form.Select
            size="sm"
            style={{ width: 160 }}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            aria-label="Mês do dashboard"
          >
            {MONTH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
          <Form.Select
            size="sm"
            style={{ width: 110 }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            aria-label="Ano do dashboard"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center m-4">
          <Spinner animation="border" variant="warning" />
        </div>
      ) : (
        <Row className="g-3 mb-4">
          {kpiCards.map((card) => (
            <Col key={card.key} xs={12} sm={6} lg={4}>
              <div
                className={`financial-kpi-card${
                  card.tone ? ` financial-kpi-card--${card.tone}` : ""
                }`}
              >
                <div className="financial-kpi-card__label">{card.label}</div>
                <div className="financial-kpi-card__value">{card.value}</div>
                <div className="financial-kpi-card__hint">{card.hint}</div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      <section className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h5 className="mb-0">Fluxo de Caixa</h5>
            <small className="text-muted">
              Entradas e saídas confirmadas por mês.
            </small>
          </div>
          <Form.Select
            size="sm"
            style={{ width: 110 }}
            value={cashFlowYear}
            onChange={(e) => setCashFlowYear(Number(e.target.value))}
            aria-label="Ano do fluxo de caixa"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Form.Select>
        </div>

        {cashFlowLoading ? (
          <div className="d-flex justify-content-center m-4">
            <Spinner animation="border" variant="warning" />
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-bordered table-hover mb-0">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th className="text-end">Entradas</th>
                  <th className="text-end">Saídas</th>
                  <th className="text-end">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {cashFlowMonths.map((row) => (
                  <tr key={row.month}>
                    <td>{row.label}</td>
                    <td className="text-end">{formatCurrency(row.entradas)}</td>
                    <td className="text-end">{formatCurrency(row.saidas)}</td>
                    <td
                      className={`text-end${
                        Number(row.saldo) < 0
                          ? " text-danger"
                          : Number(row.saldo) > 0
                            ? " text-success"
                            : ""
                      }`}
                    >
                      {formatCurrency(row.saldo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mb-2">
        <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
          <div>
            <h5 className="mb-0">Inadimplência</h5>
            <small className="text-muted">
              Cobranças vencidas ainda não quitadas.
            </small>
          </div>
          <div className="financial-overdue-totals">
            <span>
              {totals.clients_count} cliente
              {totals.clients_count === 1 ? "" : "s"}
            </span>
            <span className="mx-2 text-muted">·</span>
            <span>
              {totals.items_count} cobrança
              {totals.items_count === 1 ? "" : "s"}
            </span>
            <span className="mx-2 text-muted">·</span>
            <strong>{formatCurrency(totals.amount_due)}</strong>
          </div>
        </div>

        {overdueLoading ? (
          <div className="d-flex justify-content-center m-4">
            <Spinner animation="border" variant="warning" />
          </div>
        ) : !overdue.length ? (
          <p className="text-muted mb-0">Nenhuma cobrança inadimplente.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-bordered table-hover mb-0">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Vencimento</th>
                  <th className="text-end">Valor devido</th>
                  <th className="text-end">Dias de atraso</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {overdue.map((item) => {
                  const isSendingThis = sendingReminderId === item.id;

                  return (
                    <tr key={item.id}>
                      <td>{item.client || "—"}</td>
                      <td>{formatDate(item.due_date)}</td>
                      <td className="text-end">
                        {formatCurrency(item.amount_due)}
                      </td>
                      <td className="text-end">{item.days_overdue}</td>
                      <td className="text-end">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-secondary"
                          disabled={!!sendingReminderId || isSendingReminder}
                          onClick={() => handleSendReminder(item.id)}
                        >
                          {isSendingThis ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-1"
                              />
                              Enviando…
                            </>
                          ) : (
                            "Enviar cobrança por e-mail"
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default FinancialDashboard;
